import { isDefined } from "@clipboard-health/util-ts";
import { TIMEKEEPING_REASON_CONSTANTS } from "@src/appV2/redesign/WorkerShifts/Timekeeping/constants";
import { z } from "zod";

export const SHIFT_EXTENSION_REASONS = [
  "My relief is late",
  "There was an emergency or incident that prolonged my shift",
  "I need to finish charting/assignments",
  "I had to pass on information to the next shift",
  "Other",
] as const;

const extendShiftFormBaseSchema = z
  .object({
    hasSupervisorApproval: z.enum(["yes", "no", ""]),
    supervisorName: z.string().trim().optional(),
    isSupervisorOnShift: z.enum(["yes", "no", ""]).optional(),
    inHouseStaffName: z.string().trim().optional(),
    reasons: z.array(z.string()),
    additionalDetails: z
      .string()
      .max(200, "Additional details must be 200 characters or fewer")
      .optional(),
  })
  .superRefine((values, context) => {
    if (values.hasSupervisorApproval === "") {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select an option",
        path: ["hasSupervisorApproval"],
      });
    }

    if (
      values.hasSupervisorApproval === "yes" &&
      (!isDefined(values.supervisorName) || values.supervisorName.trim().length === 0)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Supervisor name is required",
        path: ["supervisorName"],
      });
    }

    if (values.hasSupervisorApproval === "no" && values.isSupervisorOnShift === "") {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select an option",
        path: ["isSupervisorOnShift"],
      });
    }

    if (
      values.hasSupervisorApproval === "no" &&
      values.isSupervisorOnShift === "no" &&
      (!isDefined(values.inHouseStaffName) || values.inHouseStaffName.trim().length === 0)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "In-house staff name is required",
        path: ["inHouseStaffName"],
      });
    }

    if (values.reasons.length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select at least one reason",
        path: ["reasons"],
      });
    }
  });

export type ExtendShiftFormValues = z.infer<typeof extendShiftFormBaseSchema>;

export function formatShiftExtensionReason(values: ExtendShiftFormValues): string {
  const parts: string[] = [];

  if (values.hasSupervisorApproval === "yes" && isDefined(values.supervisorName)) {
    parts.push(`Late clock out approved by ${values.supervisorName.trim()}`);
  } else if (
    values.hasSupervisorApproval === "no" &&
    values.isSupervisorOnShift === "no" &&
    isDefined(values.inHouseStaffName)
  ) {
    parts.push(
      `There was no supervisor on shift. ${values.inHouseStaffName.trim()} was an in-house staff member present during my shift.`
    );
  }

  parts.push(`Reason for shift extension: ${values.reasons.join(", ")}`);

  if (isDefined(values.additionalDetails) && values.additionalDetails.trim().length > 0) {
    parts.push(values.additionalDetails.trim());
  }

  return parts.join("\n");
}

export const extendShiftFormSchema = extendShiftFormBaseSchema.refine(
  (values) => {
    if (values.hasSupervisorApproval === "no" && values.isSupervisorOnShift === "yes") {
      return true;
    }

    return formatShiftExtensionReason(values).length <= TIMEKEEPING_REASON_CONSTANTS.MAX_CHARACTERS;
  },
  {
    message: `Combined form content exceeds the ${TIMEKEEPING_REASON_CONSTANTS.MAX_CHARACTERS} character limit`,
    path: ["additionalDetails"],
  }
);
